import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Collapse,
  Divider,
  Grid2 as Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useRecordContext, useGetMany, type Identifier } from "react-admin";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { format } from "date-fns";
import { Link as RouterLink } from "react-router-dom";
import { SummaryTable } from "./tables/SummaryTable";
import { AggSpecCorridorBanner } from "./cards/AggSpecCorridorBanner";
import { isAggregateSpecificPolicy } from "./utils/aggregateSpecific";
import { PolicyInfoCard } from "./cards/PolicyInfoCard";
import { ClientContactsCard } from "./cards/ClientContactsCard";
import { ExportMenuButton } from "./export/ExportMenuButton";
import { ExportDialog } from "./export/ExportDialog";
import { ClaimsSummaryTasks } from "./tables/ClaimsSummaryTasks";
import { ClaimsDetailTable } from "./tables/ClaimsDetailTable";
import { WatchListTable } from "./tables/WatchListTable";
import { useClaimsSummaryData } from "./useClaimsSummaryData";
import { downloadClaimsSummary } from "./export/downloadClaimsSummary";
import { Add, KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { ExportFormat, ExportOptions, ResourceName, Task, User, type Client, type Policy } from "@/types";
import Button from "@/components/ui/Button";
import { ButtonStyleType, ColorType } from "@/types/shared/uiComponents";
import { TabContent } from "@/components/common/PageLayout";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { PolicyNoteCell } from "@/components/common/PolicyNoteCell";
import { toInputDate, formatDate, DATE_FORMATS } from "@/utils/formatters/dateFormatter";
import { trackEvent, trackError, setTag } from "@/utils/clarityTracking";

/** Returns start and end dates for a policy's reporting period */
const getPolicyDateRange = (policy: Policy): { start: string; end: string } => {
  const start = toInputDate(policy.effective_start_date);
  const todayStr = format(new Date(), DATE_FORMATS.INPUT);
  const policyEndStr = toInputDate(policy.effective_end_date);
  const end = policyEndStr && policyEndStr < todayStr ? policyEndStr : todayStr;
  return { start, end };
};

export const ClaimsSummaryTab = () => {
  const client = useRecordContext<Client>();
  const notifyError = useRichApiErrorNotify();
  const { canEdit: canEditPolicy } = useResourcePermissions(ResourceName.POLICIES);

  // ==================== UI STATE ====================

  // The selected policy is held as an ID and the object is DERIVED from the live
  // `policies` query below (#7306). Storing the Policy object here instead meant the
  // ASD corridor banner read `asd_tracking` off a snapshot taken when the tab
  // mounted, so it never reflected a corridor that moved — and the banner renders
  // plausible-looking figures either way, so there was nothing to notice.
  //
  // It also fixes the inverse: the seeding effect below used to reset this on every
  // `policies` change, so a background refetch yanked the user back to policies[0]
  // and reset their date filters. An ID survives a refetch; an object reference
  // does not.
  const [selectedPolicyId, setSelectedPolicyId] = useState<Identifier>();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [claimsDetailExpanded, setClaimsDetailExpanded] = useState(false);
  const [watchListExpanded, setWatchListExpanded] = useState(false);
  const [tasksExpanded, setTasksExpanded] = useState(false);
  const [policyDetailsExpanded, setPolicyDetailsExpanded] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);

  // ==================== DATA ====================

  const {
    policies,
    policiesLoading,
    selectedPolicy,
    carrierMap,
    summaryRows,
    claimantNameById,
    watchListRows,
    stoplossClaims,
    stoplossClaimsLoading,
    reimbursements,
    fundedDateByClaimId,
    carrierVendor,
    tpaVendors,
    rxVendors,
    policyholderClient,
    sharedDataLoading,
    claimantsLoading,
    truncatedSources,
  } = useClaimsSummaryData({
    clientId: client?.id,
    selectedPolicyId,
    startDate,
    endDate,
  });

  // ASD policies get the drawdown columns + corridor banner (#3302). Declared after
  // the hook because `selectedPolicy` is now derived from its live `policies` result
  // rather than held in local state (#7306).
  const isAggSpecPolicy = isAggregateSpecificPolicy(selectedPolicy);

  // ==================== TASK USER FETCHES ====================

  const assignedUserIds = useMemo(
    () => [...new Set(tasks.map((t) => t.assigned_user_id_ref).filter((id): id is string => !!id))],
    [tasks]
  );

  const { data: assignedUsers = [] } = useGetMany<User>(
    ResourceName.USERS,
    {
      ids: assignedUserIds,
      meta: { "X-Fields": "id,full_name,preferred_name,email,user_principal_name" },
    },
    { enabled: assignedUserIds.length > 0 }
  );

  const usersMap = useMemo(
    () => assignedUsers.reduce<Record<string, User>>((acc, user) => ({ ...acc, [user.id]: user }), {}),
    [assignedUsers]
  );

  // Reimbursements scoped to the claimants shown ANYWHERE on the exhibit — the union of
  // the Summary population and the Claims Detail population. Those were the same set
  // before #6828; counting summaryRows alone let the Reimbursement sheet be disabled
  // while its data existed (review thread 27745). Mirrors the scoping in
  // downloadClaimsSummary so the toggle and the sheet agree.
  const exhibitReimbursementCount = useMemo(() => {
    const claimantIds = new Set<string>([
      ...summaryRows.map((row) => row.id),
      ...stoplossClaims.map((claim) => String(claim.claimant_id_ref)),
    ]);
    return reimbursements.filter((r) => claimantIds.has(String(r.claimant_id_ref))).length;
  }, [reimbursements, summaryRows, stoplossClaims]);

  // ==================== AUTO-EXPAND SECTIONS ====================

  // Keyed on this section's OWN rows. Under #6828 the Summary can be empty while Claims
  // Detail has rows, and collapsing it then hid exactly the rows the user needed to see
  // (review thread 27745).
  useEffect(() => {
    setClaimsDetailExpanded(stoplossClaims.length > 0);
  }, [stoplossClaims]);

  useEffect(() => {
    setWatchListExpanded(watchListRows.length > 0);
  }, [watchListRows]);

  // ==================== POLICY HANDLERS ====================

  // Seed the selection ONCE, and re-seed only if the selected policy disappears from
  // the list (deleted, or scoped away by a partner switch). Deliberately not
  // unconditional on `policies`: that reset the user's policy choice AND their date
  // filters on every background refetch of the list (#7306).
  //
  // The date range is set here and in handlePolicyChange — i.e. on a policy
  // DECISION — rather than on every `policies` change, so a refetch cannot silently
  // move the window the figures are calculated over.
  useEffect(() => {
    if (policies.length === 0) return;
    const stillPresent = selectedPolicyId !== undefined && policies.some((p) => p.id === selectedPolicyId);
    if (stillPresent) return;

    const defaultPolicy = policies[0];
    const { start, end } = getPolicyDateRange(defaultPolicy);
    setSelectedPolicyId(defaultPolicy.id);
    setStartDate(start);
    setEndDate(end);
  }, [policies, selectedPolicyId]);

  const handlePolicyChange = (event: ChangeEvent<HTMLInputElement>) => {
    const policy = policies.find((p) => p.id === event.target.value);
    if (!policy) return;

    const { start, end } = getPolicyDateRange(policy);
    setSelectedPolicyId(policy.id);
    setStartDate(start);
    setEndDate(end);
  };

  // ==================== EXPORT HANDLERS ====================

  const handleExportClick = (_format: ExportFormat) => {
    setExportDialogOpen(true);
  };

  const handleExportDownload = async (options: ExportOptions) => {
    if (!client) return;

    trackEvent("claims_summary_download");
    setTag("export_format", options.format);
    setTag("row_count", String(summaryRows.length));
    setTag("has_data", summaryRows.length > 0 ? "true" : "false");
    setTag(
      "sheets_selected",
      Object.entries(options.sheets)
        .filter(([, v]) => v)
        .map(([k]) => k)
        .join(",")
    );

    if (options.format === "pdf") {
      // TODO: PDF export — future story
      return;
    }

    try {
      await downloadClaimsSummary({
        policy: selectedPolicy!,
        client,
        options,
        summaryRows,
        claimantNameById,
        watchListRows,
        stoplossClaims,
        reimbursements,
        fundedDateByClaimId,
        tasks,
        startDate,
        endDate,
        carrierVendor,
        tpaVendors,
        rxVendors,
        policyholderClient,
        usersMap,
      });
    } catch (error) {
      trackError("claims_summary_download", "failed");
      notifyError(error);
    }
  };

  const handleTasksLoad = useCallback((loaded: Task[]) => {
    setTasks(loaded);
    setTasksExpanded(loaded.length > 0);
  }, []);

  // ==================== LOADING / EMPTY STATES ====================

  if (policiesLoading || !client?.id) {
    return (
      <TabContent>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </TabContent>
    );
  }

  if (!policies.length) {
    return (
      <TabContent>
        <Box textAlign="center" py={6}>
          <Typography variant="h6" gutterBottom>
            No Policies Found
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            This client does not have any associated policies yet.
          </Typography>
          <Button startIcon={<Add />} color={ColorType.Primary} href="#/policies/create">
            Create Policy
          </Button>
        </Box>
      </TabContent>
    );
  }

  if (!selectedPolicy) {
    return (
      <TabContent>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </TabContent>
    );
  }

  // ==================== RENDER ====================

  const hasData = summaryRows.length > 0 || stoplossClaims.length > 0 || watchListRows.length > 0 || tasks.length > 0;

  return (
    <TabContent>
      {/* ── Top row: report card (left) + Notes card (right) ───────── */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3, alignItems: "stretch", mb: 3 }}>
        <Paper
          elevation={0}
          sx={{ flex: 3, minWidth: 0, border: 1, borderColor: "divider", borderRadius: 2, overflow: "hidden" }}
        >
          {/* Title row */}
          <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Claims Summary
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Stop-loss claims activity and deductible tracking for the selected policy period
                </Typography>
              </Box>
              <ExportMenuButton onExport={handleExportClick} disabled={!hasData} />
            </Stack>
          </Box>

          <Divider />

          {/* Filter controls */}
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              gap: 1.5,
            }}
          >
            <TextField
              label="Policy"
              select
              size="small"
              value={selectedPolicy.id}
              onChange={handlePolicyChange}
              sx={{ flex: { md: 1 }, maxWidth: { md: 320 } }}
            >
              {policies.map((policy) => {
                const c = policy.vendor_id_ref ? carrierMap[policy.vendor_id_ref] : undefined;
                const carrierName = c?.company_name || "Unknown Carrier";
                const status = policy.status || "Unknown";
                const year = policy.effective_start_date
                  ? formatDate(policy.effective_start_date, DATE_FORMATS.YEAR)
                  : "N/A";

                return (
                  <MenuItem key={policy.id} value={policy.id}>
                    {carrierName} - {year} ({status})
                  </MenuItem>
                );
              })}
            </TextField>

            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: endDate }}
              sx={{ width: { xs: "100%", md: 160 } }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate }}
              sx={{ width: { xs: "100%", md: 160 } }}
            />

            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ ml: { md: "auto" }, cursor: "pointer" }}
              onClick={() => setPolicyDetailsExpanded((prev) => !prev)}
            >
              <Typography variant="caption" color="primary.main" fontWeight={500}>
                Policy details
              </Typography>
              <IconButton size="small" sx={{ p: 0 }}>
                {policyDetailsExpanded ? (
                  <KeyboardArrowDown fontSize="small" color="primary" />
                ) : (
                  <KeyboardArrowRight fontSize="small" color="primary" />
                )}
              </IconButton>
            </Stack>
          </Box>

          {/* Expandable policy & contact details */}
          <Collapse in={policyDetailsExpanded} timeout="auto" unmountOnExit>
            <Divider />
            <Box sx={{ px: 2.5, py: 2.5 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 8 }}>
                  <PolicyInfoCard policy={selectedPolicy} embedded />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <ClientContactsCard carrierVendor={carrierVendor} embedded />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: "divider" }}>
                <Button
                  component={RouterLink}
                  to={`/policies/${selectedPolicy.id}/show`}
                  variant={ButtonStyleType.Text}
                  endIcon={<ArrowRightAltIcon sx={{ fontSize: 16 }} />}
                  size="small"
                  sx={{ fontSize: "0.75rem", py: 0 }}
                >
                  View full policy
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Paper>

        {/* Notes card — policy-period summary Note (US 5370 / ADR-052 §10) */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minWidth: 0,
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Notes
          </Typography>
          <PolicyNoteCell
            policyId={String(selectedPolicy.id)}
            note={policies.find((p) => String(p.id) === String(selectedPolicy.id))?.note}
            canEdit={canEditPolicy}
            block
          />
        </Paper>
      </Box>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onDownload={handleExportDownload}
        disabledSheets={{
          // The sheet's own data, not the Summary's: a client where nobody has reached
          // their deductible has zero Summary rows and a populated Claims Detail, and
          // gating on summaryRows made those real filed claims unexportable — decision
          // #3's failure mode moved from the table to the export dialog (thread 27745).
          claimsDetail: stoplossClaims.length === 0,
          watchList: watchListRows.length === 0,
          reimbursement: exhibitReimbursementCount === 0,
          tasks: tasks.length === 0,
        }}
      />

      {/* ── Summary Table ─────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        {/* A feeding fetch came up short, so the exhibit below is built from a PARTIAL
            data set. Loud rather than silent: the period filter runs client-side, so a
            truncated fetch drops rows with no error, and the stop-loss fetch is sorted
            created_datetime DESC — which means the rows lost are the oldest, i.e. a
            prior-period report is the one that quietly comes up short (#6823 review). */}
        {truncatedSources.length > 0 && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            <AlertTitle sx={{ fontSize: "0.875rem", fontWeight: 600, mb: 0.25 }}>This report is incomplete</AlertTitle>
            <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
              {truncatedSources.map((s) => `${s.label}: ${s.fetched} of ${s.total}`).join(" · ")}. Figures below omit
              the remainder — do not send this to a client until the data set fits.
            </Typography>
          </Alert>
        )}
        {/* Corridor context first: on an ASD policy this is what explains a column of
            $0 Owed for claimants who ARE over their specific deductible (#3302). */}
        {isAggSpecPolicy && <AggSpecCorridorBanner policy={selectedPolicy} />}
        <SummaryTable
          rows={summaryRows}
          loading={sharedDataLoading}
          policyId={String(selectedPolicy.id)}
          showAggSpec={isAggSpecPolicy}
        />
      </Box>

      {/* ── Claims Detail ─────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <ClaimsDetailTable
          claimantNameById={claimantNameById}
          claims={stoplossClaims}
          loading={stoplossClaimsLoading}
          isExpanded={claimsDetailExpanded}
          onToggle={() => setClaimsDetailExpanded((prev) => !prev)}
        />
      </Box>

      {/* ── Watch List + Tasks ────────────────────────────────────── */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <WatchListTable
            rows={watchListRows}
            loading={claimantsLoading}
            isExpanded={watchListExpanded}
            onToggle={() => setWatchListExpanded((prev) => !prev)}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 5 }}>
          <ClaimsSummaryTasks
            clientId={client.id}
            onTasksLoad={handleTasksLoad}
            isExpanded={tasksExpanded}
            onToggle={() => setTasksExpanded((prev) => !prev)}
          />
        </Grid>
      </Grid>
    </TabContent>
  );
};
